import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import type { ListLotsParams, ListNotesParams } from '@/lib/api/product-lot-api'
import { productLotApi } from '@/lib/api/product-lot-api'

// ============================================================================
// Query keys
// ============================================================================

export const productLotKeys = {
    all: ['product-lots'] as const,
    lots: () => [...productLotKeys.all, 'lots'] as const,
    lotList: (params: ListLotsParams) =>
        [...productLotKeys.lots(), 'list', params] as const,
    lotDetail: (id: string) =>
        [...productLotKeys.lots(), 'detail', id] as const,
    lotHistory: (id: string) =>
        [...productLotKeys.lots(), 'history', id] as const,
    lotLineage: (id: string) =>
        [...productLotKeys.lots(), 'lineage', id] as const,

    inNotes: () => [...productLotKeys.all, 'in-notes'] as const,
    inNoteList: (params: ListNotesParams) =>
        [...productLotKeys.inNotes(), 'list', params] as const,
    inNoteDetail: (id: string) =>
        [...productLotKeys.inNotes(), 'detail', id] as const,

    outNotes: () => [...productLotKeys.all, 'out-notes'] as const,
    outNoteList: (params: ListNotesParams) =>
        [...productLotKeys.outNotes(), 'list', params] as const,
    outNoteDetail: (id: string) =>
        [...productLotKeys.outNotes(), 'detail', id] as const,
}

// ============================================================================
// Lot queries
// ============================================================================

export function useProductLotsQuery(
    params: ListLotsParams = {},
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: productLotKeys.lotList(params),
        queryFn: () => productLotApi.listLots(params),
    })
}

/**
 * Infinite variant cho mobile "Xem thêm" pattern. Accumulate pages 1..N.
 * `enabled` false-safe: khi caller pass false (vd viewport không phải mobile),
 * hook không fetch.
 */
export function useProductLotsInfiniteQuery(
    params: Omit<ListLotsParams, 'page'> = {},
    options?: { enabled?: boolean },
) {
    return useInfiniteQuery({
        enabled: options?.enabled ?? true,
        queryKey: [...productLotKeys.lots(), 'infinite', params] as const,
        queryFn: ({ pageParam }) =>
            productLotApi.listLots({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function useProductLotQuery(id: string | undefined) {
    return useQuery({
        enabled: !!id,
        queryKey: productLotKeys.lotDetail(id ?? ''),
        queryFn: () => productLotApi.getLot(id as string),
    })
}

export function useProductLotHistoryQuery(id: string | undefined) {
    return useInfiniteQuery({
        enabled: !!id,
        queryKey: productLotKeys.lotHistory(id ?? ''),
        queryFn: ({ pageParam }) =>
            productLotApi.getLotHistory(id as string, {
                page: pageParam,
                per_page: 20,
            }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

// ============================================================================
// Stock-in note queries
// ============================================================================

export function useProductLotInNotesQuery(
    params: ListNotesParams = {},
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: productLotKeys.inNoteList(params),
        queryFn: () => productLotApi.listInNotes(params),
    })
}

/**
 * Infinite variant cho mobile "Xem thêm" pattern. Accumulate pages 1..N.
 * `enabled` false-safe: khi caller pass false (vd viewport không phải mobile),
 * hook không fetch.
 */
export function useProductLotInNotesInfiniteQuery(
    params: Omit<ListNotesParams, 'page'> = {},
    options?: { enabled?: boolean },
) {
    return useInfiniteQuery({
        enabled: options?.enabled ?? true,
        queryKey: [...productLotKeys.inNotes(), 'infinite', params] as const,
        queryFn: ({ pageParam }) =>
            productLotApi.listInNotes({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function useProductLotInNoteQuery(id: string | undefined) {
    return useQuery({
        enabled: !!id,
        queryKey: productLotKeys.inNoteDetail(id ?? ''),
        queryFn: () => productLotApi.getInNote(id as string),
    })
}

// ============================================================================
// Stock-out note queries
// ============================================================================

export function useProductLotOutNotesQuery(
    params: ListNotesParams = {},
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: productLotKeys.outNoteList(params),
        queryFn: () => productLotApi.listOutNotes(params),
    })
}

/**
 * Infinite variant cho mobile "Xem thêm" pattern. Accumulate pages 1..N.
 * `enabled` false-safe: khi caller pass false (vd viewport không phải mobile),
 * hook không fetch.
 */
export function useProductLotOutNotesInfiniteQuery(
    params: Omit<ListNotesParams, 'page'> = {},
    options?: { enabled?: boolean },
) {
    return useInfiniteQuery({
        enabled: options?.enabled ?? true,
        queryKey: [...productLotKeys.outNotes(), 'infinite', params] as const,
        queryFn: ({ pageParam }) =>
            productLotApi.listOutNotes({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function useProductLotOutNoteQuery(id: string | undefined) {
    return useQuery({
        enabled: !!id,
        queryKey: productLotKeys.outNoteDetail(id ?? ''),
        queryFn: () => productLotApi.getOutNote(id as string),
    })
}

// Lineage 5 hop upstream. staleTime 5 min vì lịch sử ít khi đổi.
export function useProductLotLineage(lotId: string, enabled: boolean = true) {
    return useQuery({
        enabled: enabled && Boolean(lotId),
        queryKey: productLotKeys.lotLineage(lotId),
        queryFn: () => productLotApi.getLineage(lotId),
        staleTime: 5 * 60 * 1000,
    })
}
